/**
 * Which card payments to sort out are on screen (2026-10-26).
 *
 * The list on Sales grew to every payment BlueSage had seen without Shekko —
 * staging's first week on the store's live account brought almost two hundred
 * from before Shekko existed, and the page ran on for screens. So it shows the
 * newest few, both kinds together in the order they happened, until it is
 * opened in full.
 */

/** How many show before the list is opened. */
export const ATTENTION_SHOWN = 5;

export interface AttentionRow<T> {
  readonly kind: 'unmatched' | 'flagged';
  readonly at: number;
  readonly item: T;
}

/** Both kinds as one list, newest first; a date that cannot be read goes last. */
export function newestFirst<U, F>(
  unmatched: readonly U[],
  flagged: readonly F[],
  when: {
    readonly unmatched: (item: U) => string | Date;
    readonly flagged: (item: F) => string | Date;
  },
): AttentionRow<U | F>[] {
  const time = (value: string | Date): number => {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
  };
  return [
    ...unmatched.map((item) => ({
      kind: 'unmatched' as const,
      at: time(when.unmatched(item)),
      item,
    })),
    ...flagged.map((item) => ({ kind: 'flagged' as const, at: time(when.flagged(item)), item })),
  ].sort((a, b) => (a.at === b.at ? 0 : b.at > a.at ? 1 : -1));
}

/** What is on screen: everything once opened, otherwise the newest few. */
export function shownRows<T>(
  rows: readonly T[],
  open: boolean,
  limit = ATTENTION_SHOWN,
): readonly T[] {
  return open ? rows : rows.slice(0, limit);
}
